import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { register, getRegistrationStatus } from "../services/auth.ts";
import { confirmPayment } from "../services/payments.ts";
import StripeCheckout from "../components/StripeCheckout.tsx";

const Register: React.FC = () => {
  const [country, setCountry] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [postcode, setPostcode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showStripe, setShowStripe] = useState(false);
  const [pendingRegister, setPendingRegister] = useState<{
    email: string,
    password: string,
    country: string,
    addressLine1: string,
    addressLine2: string,
    city: string,
    postcode: string
  } | null>(null);
  const [stripePaid, setStripePaid] = useState(false);
  const [activating, setActivating] = useState(false);
  const navigate = useNavigate();
  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  };

  // Resume a registration that was interrupted (tab closed / page reloaded after payment).
  useEffect(() => {
    const savedEmail = localStorage.getItem("registration_email");
    if (!savedEmail) return;
    (async () => {
      try {
        const { data } = await getRegistrationStatus(savedEmail);
        if (data.status === "active") {
          localStorage.removeItem("registration_email");
          setSuccess("Registration successful! You can now log in.");
          setStripePaid(true);
        } else if (data.status === "pending") {
          setEmail(savedEmail);
          setError(
            "You started registering but payment wasn't completed. Enter your password below to continue."
          );
        } else {
          localStorage.removeItem("registration_email");
        }
      } catch {
        // Status check failed silently; user can just retry registering.
      }
    })();
    return stopPolling;
  }, []);

  const pollForActivation = (targetEmail: string, timeoutMessage: string) => {
    setActivating(true);
    setError("");
    let attempts = 0;
    stopPolling();
    pollTimer.current = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await getRegistrationStatus(targetEmail);
        if (data.status === "active") {
          stopPolling();
          setActivating(false);
          localStorage.removeItem("registration_email");
          setSuccess("Registration successful! You can now log in.");
          setStripePaid(true);
        } else if (attempts >= 10) {
          stopPolling();
          setActivating(false);
          setError(timeoutMessage);
        }
      } catch {
        // Transient network error while polling; keep trying until attempts run out.
      }
    }, 2000);
  };

  // Subscription fee logic
  const getSubscriptionFee = () => {
    switch (country) {
      case "UK":
        return { amount: 700, label: "£7" };
      case "India":
        return { amount: 1000 * 100, label: "Rs. 1000" };
      case "US":
        return { amount: 1000, label: "$10" };
      default:
        return { amount: 700, label: "£7" };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!country) {
      setError("Please select your country");
      return;
    }
    if (!addressLine1 || !city || !postcode) {
      setError("Please complete your address");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    if (!termsAccepted) {
      setError("Please accept the Privacy Policy and Terms of Service to continue");
      return;
    }
    setLoading(true);
    localStorage.setItem("registration_email", email);
    try {
      const { data } = await register(email, password, country, termsAccepted);
      if (data.status === "active") {
        localStorage.removeItem("registration_email");
        setSuccess("Registration successful! You can now log in.");
        setStripePaid(true);
      } else {
        setPendingRegister({ email, password, country, addressLine1, addressLine2, city, postcode });
        setShowStripe(true);
      }
    } catch (err: any) {
      localStorage.removeItem("registration_email");
      setError(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  // Called after successful Stripe payment - the account already exists (pending).
  // Confirm this specific PaymentIntent by ID right away instead of waiting on the
  // webhook, which only serves as a fallback if this call itself fails.
  const handleStripeSuccess = async (paymentIntentId: string) => {
    setShowStripe(false);
    const targetEmail = pendingRegister?.email;
    setPendingRegister(null);
    if (!targetEmail) return;

    try {
      const { data } = await confirmPayment(targetEmail, paymentIntentId);
      if (data.status === "active") {
        localStorage.removeItem("registration_email");
        setSuccess("Registration successful! You can now log in.");
        setStripePaid(true);
        return;
      }
    } catch {
      // Confirmation call itself failed (network blip) - fall back to polling below.
    }

    pollForActivation(
      targetEmail,
      "Payment received. Activation is taking longer than usual — please refresh this page in a minute, or contact support if it doesn't complete."
    );
  };

  // Called if Stripe payment fails
  const handleStripeFailure = (msg: string) => {
    setShowStripe(false);
    setStripePaid(false);
    setError(msg || "Payment failed. Registration not completed.");
    setPendingRegister(null);
  };

  // Called when the payment outcome couldn't be confirmed (e.g. network dropped after Stripe
  // processed it). We don't know if the charge went through, so check with the backend instead
  // of telling the user it failed.
  const handleStripeUncertain = () => {
    setShowStripe(false);
    if (pendingRegister) {
      pollForActivation(
        pendingRegister.email,
        "We couldn't confirm your payment status. If you were charged, your account will activate shortly — refresh this page in a minute. If not, please try registering again."
      );
    }
    setPendingRegister(null);
  };

  const labelClass = "text-xl font-semibold mb-1 text-gray-500 font-body";
  const fieldClass = "rounded-lg px-4 py-2 text-xl font-body border border-border focus:border-accent focus:outline-none bg-white text-ink";

  return (
    <div className="min-h-screen flex flex-col items-center px-6 py-12">
      <h2 className="text-5xl font-bold text-center mb-6 text-ink font-display">Register</h2>
      {success && stripePaid && (
        <div className="mb-6 flex flex-col items-center">
          <div className="text-success text-xl font-semibold font-body text-center">{success}</div>
          <button
            className="mt-4 bg-primary text-white font-semibold font-body py-2 px-6 rounded-lg hover:bg-ink transition-colors duration-200"
            onClick={() => navigate("/login")}
          >
            Go to Login
          </button>
        </div>
      )}
      {country && (
        <div className="mb-4 max-w-3xl w-full text-xl text-center text-primary font-semibold font-body bg-lightbg rounded-lg p-3 border border-border">
          Subscription for <span className="font-bold">{getSubscriptionFee().label}/month</span> to access all features. You will be prompted to pay after submitting this form.
        </div>
      )}
      <form
        onSubmit={handleSubmit}
        className="grid grid-cols-1 md:grid-cols-2 gap-8 bg-white rounded-lg p-10 w-full shadow-none border border-border min-w-[350px] md:min-w-[600px] lg:min-w-[800px]"
      >
        <div className="flex flex-col col-span-1">
          <label className={labelClass}>Country</label>
          <select
            value={country}
            onChange={e => setCountry(e.target.value)}
            required
            className={fieldClass}
          >
            <option value="">Select your country</option>
            <option value="UK">United Kingdom</option>
            <option value="India">India</option>
            <option value="US">United States</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="flex flex-col col-span-1">
          <label className={labelClass}>Address Line 1</label>
          <input
            type="text"
            value={addressLine1}
            onChange={e => setAddressLine1(e.target.value)}
            required
            className={fieldClass}
            placeholder="Address line 1"
          />
        </div>
        <div className="flex flex-col col-span-1">
          <label className={labelClass}>Address Line 2</label>
          <input
            type="text"
            value={addressLine2}
            onChange={e => setAddressLine2(e.target.value)}
            className={fieldClass}
            placeholder="Address line 2 (optional)"
          />
        </div>
        <div className="flex flex-col col-span-1">
          <label className={labelClass}>City</label>
          <input
            type="text"
            value={city}
            onChange={e => setCity(e.target.value)}
            required
            className={fieldClass}
            placeholder="City"
          />
        </div>
        <div className="flex flex-col col-span-1">
          <label className={labelClass}>Postcode</label>
          <input
            type="text"
            value={postcode}
            onChange={e => setPostcode(e.target.value)}
            required
            className={fieldClass}
            placeholder="Postcode"
          />
        </div>
        <div className="flex flex-col col-span-1">
          <label className={labelClass}>Email</label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            className={fieldClass}
            placeholder="Enter your email"
          />
        </div>
        <div className="flex flex-col col-span-1">
          <label className={labelClass}>Password</label>
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            className={fieldClass}
            placeholder="Enter your password"
          />
        </div>
        <div className="flex flex-col col-span-1">
          <label className={labelClass}>Confirm Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            required
            className={fieldClass}
            placeholder="Re-enter your password"
          />
        </div>
        <div className="md:col-span-2 flex items-start gap-2">
          <input
            type="checkbox"
            id="terms-accepted"
            checked={termsAccepted}
            onChange={e => setTermsAccepted(e.target.checked)}
            required
            className="mt-1"
          />
          <label htmlFor="terms-accepted" className="text-lg font-body text-gray-500">
            I agree to the{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline">
              Privacy Policy
            </a>{" "}
            and{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary underline">
              Terms of Service
            </a>
          </label>
        </div>
        <div className="md:col-span-2 flex justify-center">
          <button
            type="submit"
            disabled={loading || !termsAccepted}
            className="bg-primary text-white text-xl font-semibold font-body py-2 px-8 rounded-lg hover:bg-ink transition-colors duration-200 min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Registering..." : `Register & Pay ${getSubscriptionFee().label}`}
          </button>
        </div>
      </form>
      {error && (
        <div className="mt-4 text-incorrect text-xl font-semibold font-body text-center">
          {typeof error === "string"
            ? error
            : error && error.msg
              ? error.msg
              : error && error.detail
                ? error.detail
                : JSON.stringify(error)}
        </div>
      )}
      {activating && (
        <div className="mt-4 text-primary text-xl font-semibold font-body text-center">
          Payment received. Activating your account...
        </div>
      )}
      {showStripe && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-lg w-full relative border border-border">
            <button
              className="absolute top-2 right-2 text-gray-400 hover:text-ink text-3xl font-bold"
              onClick={() => setShowStripe(false)}
            >
              ×
            </button>
            <StripeCheckout
              amount={getSubscriptionFee().amount}
              currency={country === "India" ? "inr" : country === "US" ? "usd" : "gbp"}
              country={country}
              email={email}
              onSuccess={handleStripeSuccess}
              onFailure={handleStripeFailure}
              onUncertain={handleStripeUncertain}
              description={`Monthly Subscription (${getSubscriptionFee().label})`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Register;
